import { cn } from '@massilia/ui'
import {
  addMonths,
  differenceInCalendarDays,
  format,
  isSameDay,
  parseISO,
  startOfMonth,
  startOfToday,
} from 'date-fns'
import { fr } from 'date-fns/locale'

import { centsToEuros, formatPrice } from '@/utils/format-price'

interface InstallmentTimelineProps {
  balanceDeadline: string
  departureDate: string
  depositDeadline?: string
  minimumDepositCents?: number
}

const DEPOSIT_PILL_FALLBACK_PERCENT = 5
const DEPOSIT_HINT_TEXT = 'La date limite de versement sera précisée après réservation'

export function InstallmentTimeline({
  balanceDeadline,
  departureDate,
  depositDeadline,
  minimumDepositCents,
}: InstallmentTimelineProps) {
  const today = startOfToday()
  const departure = parseDate(departureDate)
  const balance = parseDate(balanceDeadline)
  const deposit = depositDeadline ? parseDate(depositDeadline) : null
  const hasDepositInfo = minimumDepositCents != null
  const hasDepositDate = deposit != null && minimumDepositCents != null

  if (!departure || !balance) return null

  const totalSpanDays = differenceInCalendarDays(departure, today)
  if (totalSpanDays <= 0) return null

  const toPercent = (date: Date): number => {
    const days = differenceInCalendarDays(date, today)
    return Math.max(0, Math.min(100, (days / totalSpanDays) * 100))
  }

  const monthlyTickDates = collectMonthlyTickDates(today, balance, deposit)
  const depositPercent = deposit ? toPercent(deposit) : DEPOSIT_PILL_FALLBACK_PERCENT
  const balancePercent = toPercent(balance)

  const depositLabel = hasDepositDate
    ? `Acompte minimum de ${formatPrice(centsToEuros(minimumDepositCents))} avant le ${formatLabelDate(deposit)}`
    : hasDepositInfo
      ? `Montant minimum de l'acompte : ${formatPrice(centsToEuros(minimumDepositCents))}`
      : null
  const balanceLabel = `Solde au plus tard le ${formatLabelDate(balance)}`

  return (
    <div className="border-brand-500 mt-6 w-full rounded-lg border-2 bg-white p-4 sm:p-5">
      <p className="text-gray-light-700 mb-4 text-xs font-medium sm:text-sm">
        Échéancier de paiement
      </p>

      {/* Mobile: stacked deposit label */}
      {depositLabel && (
        <div className="mb-3 sm:hidden">
          <p className="text-gray-light-900 text-xs font-medium">{depositLabel}</p>
          {!hasDepositDate && (
            <p className="text-gray-light-500 mt-0.5 text-[11px] italic">{DEPOSIT_HINT_TEXT}</p>
          )}
        </div>
      )}

      {/* Desktop: floating deposit label above the bar */}
      {depositLabel && (
        <div className="relative hidden h-12 sm:block">
          <FloatingLabel
            align="bottom"
            hint={hasDepositDate ? undefined : DEPOSIT_HINT_TEXT}
            leftPercent={depositPercent}
            showConnector={hasDepositDate}
            text={depositLabel}
          />
        </div>
      )}

      {/* Bar with ticks */}
      <div className="relative h-4 w-full">
        <div className="bg-gray-light-300 absolute inset-x-0 top-1/2 h-1 -translate-y-1/2 rounded-full" />
        <Endpoint leftPercent={0} />
        <Endpoint leftPercent={100} />
        {monthlyTickDates.map((date) => (
          <Tick key={date.toISOString()} leftPercent={toPercent(date)} />
        ))}
        {hasDepositDate && <Tick highlighted leftPercent={depositPercent} />}
        <Tick highlighted leftPercent={balancePercent} />
      </div>

      {/* Endpoint labels */}
      <div className="text-gray-light-500 mt-2 flex justify-between text-[11px] font-medium sm:text-xs">
        <span>Aujourd&apos;hui</span>
        <span>Départ le {format(departure, 'dd/MM/yyyy')}</span>
      </div>

      {/* Desktop: floating balance label below the bar */}
      <div className="relative mt-2 hidden h-12 sm:block">
        <FloatingLabel align="top" leftPercent={balancePercent} text={balanceLabel} />
      </div>

      {/* Mobile: stacked balance label */}
      <p className="text-gray-light-900 mt-3 text-xs font-medium sm:hidden">{balanceLabel}</p>

      <p className="text-gray-light-900 mt-3 text-center text-[11px] font-medium sm:text-xs">
        1 paiement par mois minimum
      </p>
    </div>
  )
}

function collectMonthlyTickDates(today: Date, balance: Date, deposit: Date | null): Date[] {
  const ticks: Date[] = []
  let cursor = startOfMonth(addMonths(today, 1))
  while (cursor.getTime() < balance.getTime()) {
    if (!deposit || !isSameDay(cursor, deposit)) ticks.push(cursor)
    cursor = addMonths(cursor, 1)
  }
  return ticks
}

function Endpoint({ leftPercent }: { leftPercent: number }) {
  return (
    <div
      className="bg-brand-600 absolute top-1/2 size-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full"
      style={{ left: `${leftPercent}%` }}
    />
  )
}

function FloatingLabel({
  align,
  hint,
  leftPercent,
  showConnector = true,
  text,
}: {
  align: 'bottom' | 'top'
  hint?: string
  leftPercent: number
  showConnector?: boolean
  text: string
}) {
  const clamped = Math.max(0, Math.min(100, leftPercent))
  const anchorRight = clamped > 70
  const anchorLeft = clamped < 30

  const labelStyle: React.CSSProperties = anchorRight
    ? { right: `${100 - clamped}%` }
    : anchorLeft
      ? { left: `${clamped}%` }
      : { left: `${clamped}%`, transform: 'translateX(-50%)' }

  return (
    <>
      <div
        className={cn('absolute flex flex-col gap-0.5', align === 'top' ? 'top-2' : 'bottom-2')}
        style={labelStyle}
      >
        <span className="border-brand-100 bg-brand-50 text-gray-light-900 rounded-md border px-2.5 py-1 text-xs font-medium whitespace-nowrap shadow-sm">
          {text}
        </span>
        {hint && (
          <span className="text-gray-light-500 text-[11px] whitespace-nowrap italic">{hint}</span>
        )}
      </div>
      {showConnector && (
        <span
          className={cn('bg-brand-300 absolute h-2 w-px', align === 'top' ? 'top-0' : 'bottom-0')}
          style={{ left: `${clamped}%`, transform: 'translateX(-50%)' }}
        />
      )}
    </>
  )
}

function formatLabelDate(date: Date): string {
  return format(date, 'd MMM yyyy', { locale: fr })
}

function parseDate(value: string): Date | null {
  try {
    const parsed = value.includes('T') ? parseISO(value) : parseISO(`${value}T00:00:00`)
    return isNaN(parsed.getTime()) ? null : parsed
  } catch {
    return null
  }
}

function Tick({
  highlighted = false,
  leftPercent,
}: {
  highlighted?: boolean
  leftPercent: number
}) {
  return (
    <div
      className={cn(
        'absolute top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-sm',
        highlighted ? 'bg-brand-600 h-3.5 w-0.5' : 'bg-gray-light-500 h-2 w-px'
      )}
      style={{ left: `${leftPercent}%` }}
    />
  )
}
